import React, { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  HelpCircle, Shield, Bot, Swords, Crown, BarChart3, Zap,
  CheckCircle, Target, Rocket, Globe, ChevronRight,
} from "lucide-react";

const SECTIONS_EN = [
  {
    icon: Shield,
    color: "text-violet-400",
    title: "Regime Coverage",
    steps: [
      "Shows the 6 market regimes (Trend Up, Trend Down, Range LV, Range HV, Breakout, Mean Revert).",
      "Green = a validated survivor strategy exists for that regime.",
      "Red = no strategy found yet (a gap).",
      "Click \"Mine Gaps\" to launch diversity mining — the system auto-discovers strategies for uncovered regimes.",
      "Mining now uses adaptive random search (not grid) + 6 new structure-based families (Stop Hunt, Wick Rejection, Trend Pullback, Momentum Continuation, ATR Compression, Range Compression).",
    ],
  },
  {
    icon: Shield,
    color: "text-amber-400",
    title: "Auto-Validator",
    steps: [
      "Automatically tests unvalidated EMERALD strategies through a 5-gate pipeline.",
      "Click \"Start Auto-Validate\" to begin. The system picks the next unvalidated EMERALD and runs it through all gates.",
      "Strategies that pass become \"Survivors\" — battle-tested and ready for deployment.",
      "The panel shows pass/fail counts and a live log of progress.",
    ],
  },
  {
    icon: Swords,
    color: "text-violet-400",
    title: "Evolution Tournament",
    steps: [
      "A 3-round competition to find the absolute best strategy.",
      "Round 1: 180-day backtest. Round 2: 365 days. Final: 730 days.",
      "Tests across ETH, BTC, and SOL to ensure robustness.",
      "Click \"Start Tournament\" to begin. The winner is crowned Champion.",
    ],
  },
  {
    icon: Bot,
    color: "text-cyan-400",
    title: "Autopilot",
    steps: [
      "An autonomous agent that monitors live market regimes for ETH, BTC, and SOL.",
      "Click \"Engage Autopilot\" to enable. It checks the market every 5 minutes.",
      "If the regime changes and a better survivor exists, it auto-rotates the deployed strategy.",
      "The Live Regime Map shows current market conditions per asset.",
    ],
  },
  {
    icon: BarChart3,
    color: "text-emerald-400",
    title: "Live Portfolio",
    steps: [
      "Shows your live paper-trading portfolio performance based on a $100K starting balance.",
      "Displays P&L, return %, and an equity curve chart per deployed agent.",
      "Compares your performance against an ETH Buy & Hold benchmark.",
    ],
  },
  {
    icon: Crown,
    color: "text-amber-400",
    title: "Deploy Champion ($100K)",
    steps: [
      "One-click deployment of the tournament champion with a clean $100K portfolio.",
      "Clears all previous trades and deploys the #1 strategy fresh.",
      "Use this after running a tournament to start tracking the best strategy.",
    ],
  },
  {
    icon: Rocket,
    color: "text-emerald-400",
    title: "Validated Survivors",
    steps: [
      "Appears when agents are not running. Shows all strategies that passed the 5-gate validation.",
      "Select one or more strategies, set a starting balance, and click \"Deploy\".",
      "Regime-matched strategies are highlighted — they work best in the current market condition.",
    ],
  },
];

const SECTIONS_GR = [
  {
    icon: Shield,
    color: "text-violet-400",
    title: "Regime Coverage",
    steps: [
      "6 market regimes (Trend Up, Trend Down, Range LV, Range HV, Breakout, Mean Revert).",
      "Green = Strategy Survivor. Red = Gap.",
      "\"Mine Gaps\": Mining strategies.",
    ],
  },
  {
    icon: Shield,
    color: "text-amber-400",
    title: "Auto-Validator",
    steps: [
      "5-gate pipeline.",
      "\"Start Auto-Validate\".",
      "Survivors.",
      "Pass/fail counts, live log.",
    ],
  },
  {
    icon: Swords,
    color: "text-violet-400",
    title: "Evolution Tournament",
    steps: [
      "3 competition. Round 1: 180 days. Round 2: 365 days. Final: 730 days.",
      "ETH, BTC, SOL.",
      "\"Start Tournament\" Champion.",
    ],
  },
  {
    icon: Bot,
    color: "text-cyan-400",
    title: "Autopilot",
    steps: [
      "ETH, BTC, SOL.",
      "\"Engage Autopilot\". 5 minutes.",
      "Auto-rotation strategy.",
      "Live Regime Map.",
    ],
  },
  {
    icon: BarChart3,
    color: "text-emerald-400",
    title: "Live Portfolio",
    steps: [
      "$100K portfolio.",
      "P&L, return %, equity curve.",
      "ETH Buy & Hold benchmark.",
    ],
  },
  {
    icon: Crown,
    color: "text-amber-400",
    title: "Deploy Champion ($100K)",
    steps: [
      "$100K.",
      "Trades clear, #1 strategy.",
    ],
  },
  {
    icon: Rocket,
    color: "text-emerald-400",
    title: "Validated Survivors",
    steps: [
      "5-gate validation strategies.",
      "Deploy.",
      "Regime-matched.",
    ],
  },
];

const QUICK_START_EN = [
  { step: "1", text: "Run a Tournament to find the best strategy", icon: Swords },
  { step: "2", text: "Click \"Deploy Champion ($100K)\" to start paper trading", icon: Crown },
  { step: "3", text: "Enable Autopilot for hands-free regime rotation", icon: Bot },
  { step: "4", text: "Mine Gaps to discover strategies for all market conditions", icon: Zap },
  { step: "5", text: "Start Auto-Validate to test new discoveries automatically", icon: Shield },
];

const QUICK_START_GR = [
  { step: "1", text: "Tournament Champion", icon: Swords },
  { step: "2", text: "\"Deploy Champion ($100K)\"", icon: Crown },
  { step: "3", text: "Autopilot", icon: Bot },
  { step: "4", text: "Mine Gaps", icon: Zap },
  { step: "5", text: "Auto-Validate", icon: Shield },
];

export function HowToGuide() {
  const [lang, setLang] = useState("en");
  const sections = lang === "en" ? SECTIONS_EN : SECTIONS_GR;
  const quickStart = lang === "en" ? QUICK_START_EN : QUICK_START_GR;

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="border-cyan-700/50 text-cyan-400 hover:bg-cyan-950/30 hover:text-cyan-300 h-8 gap-1.5"
          data-testid="how-to-guide-btn"
        >
          <HelpCircle className="w-3.5 h-3.5" />
          How-To
        </Button>
      </DialogTrigger>
      <DialogContent aria-describedby="howto-desc" className="max-w-2xl max-h-[85vh] overflow-y-auto bg-zinc-950 border-zinc-800 text-zinc-200" data-testid="how-to-guide-modal">
        <DialogHeader>
          <div className="flex items-center justify-between pr-6">
            <DialogTitle className="text-lg font-bold text-zinc-100 flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-cyan-400" />
              SKYNET TRADER Guide
            </DialogTitle>
            <div className="flex gap-1">
              <Button
                size="sm"
                variant={lang === "en" ? "default" : "outline"}
                onClick={() => setLang("en")}
                className={`h-6 text-[10px] px-2 ${lang === "en" ? "bg-cyan-700 text-white" : "border-zinc-700 text-zinc-400"}`}
                data-testid="lang-en-btn"
              >
                EN
              </Button>
              <Button
                size="sm"
                variant={lang === "gr" ? "default" : "outline"}
                onClick={() => setLang("gr")}
                className={`h-6 text-[10px] px-2 ${lang === "gr" ? "bg-cyan-700 text-white" : "border-zinc-700 text-zinc-400"}`}
                data-testid="lang-gr-btn"
              >
                GR
              </Button>
            </div>
          </div>
        </DialogHeader>
        <p id="howto-desc" className="sr-only">Step-by-step guide to all SKYNET TRADER features</p>

        {/* Quick Start */}
        <div className="p-3 bg-cyan-950/20 border border-cyan-800/30 rounded-sm" data-testid="quick-start-section">
          <h3 className="text-xs font-bold text-cyan-400 mb-2.5 flex items-center gap-1.5">
            <Target className="w-3.5 h-3.5" />
            {lang === "en" ? "QUICK START" : "QUICK START"}
          </h3>
          <div className="space-y-2">
            {quickStart.map((qs) => {
              const Icon = qs.icon;
              return (
                <div key={qs.step} className="flex items-center gap-2.5 text-xs">
                  <Badge className="w-5 h-5 flex items-center justify-center bg-cyan-500/20 text-cyan-300 border-cyan-500/40 text-[10px] p-0 shrink-0">
                    {qs.step}
                  </Badge>
                  <Icon className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
                  <span className="text-zinc-300">{qs.text}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Panel Sections */}
        <div className="space-y-3" data-testid="guide-sections">
          {sections.map((section, idx) => {
            const Icon = section.icon;
            return (
              <div key={idx} className="p-3 bg-zinc-900/60 border border-zinc-800/60 rounded-sm">
                <h3 className={`text-xs font-bold mb-2 flex items-center gap-1.5 ${section.color}`}>
                  <Icon className="w-3.5 h-3.5" />
                  {section.title}
                </h3>
                <ul className="space-y-1.5">
                  {section.steps.map((step, i) => (
                    <li key={i} className="flex items-start gap-2 text-[11px] text-zinc-400">
                      <ChevronRight className="w-3 h-3 text-zinc-600 shrink-0 mt-0.5" />
                      <span>{step}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Tip */}
        <div className="p-2.5 bg-amber-950/20 border border-amber-800/30 rounded-sm text-[11px] text-amber-300/80" data-testid="guide-tip">
          <span className="font-bold text-amber-400">
            {lang === "en" ? "TIP:" : "TIP:"}
          </span>{" "}
          {lang === "en"
            ? "The recommended workflow is: Tournament > Deploy Champion > Autopilot. This gives you the strongest strategy running on autopilot with regime-aware rotation."
            : "Tournament > Deploy Champion > Autopilot."}
        </div>
      </DialogContent>
    </Dialog>
  );
}
